import { Injectable, inject } from '@angular/core';
import { ApiNotificationsService, LoggerService } from '@shared';
import { catchError, firstValueFrom, EMPTY } from 'rxjs';

/**
 * Registers the FCM token with the backend (Postgres) so new-order push notifications
 * are delivered. Also stores the token so it can be unregistered on logout.
 */
@Injectable({ providedIn: 'root' })
export class FcmBackendRegistrationService {
  private readonly apiNotifications = inject(ApiNotificationsService);
  private readonly logger = inject(LoggerService);
  /** Last token successfully registered with the backend (for logout unregister). */
  private lastRegisteredToken: string | null = null;
  /** Token already POSTed this session — avoids duplicate register-token calls. */
  private backendRegisteredToken: string | null = null;

  /**
   * Register the FCM token with the backend. Call when FCM provides a token (e.g. after login).
   */
  registerToken(token: string): void {
    const trimmed = token?.trim();
    if (!trimmed) {
      return;
    }
    if (this.backendRegisteredToken === trimmed) {
      return;
    }
    this.lastRegisteredToken = trimmed;
    this.logger.info(
      `[FCM-E2E][Vendor] POST notifications/register-token tokenLen=${trimmed.length}`,
    );
    this.apiNotifications.registerFCMToken(trimmed).pipe(
      catchError((err) => {
        this.logger.warn(
          `[FCM-E2E][Vendor] register-token API failed: ${err?.message || err}`,
        );
        return EMPTY;
      }),
    ).subscribe(() => {
      this.backendRegisteredToken = trimmed;
      this.logger.info('[FCM-E2E][Vendor] register-token API success');
    });
  }

  /**
   * Unregister the last FCM token from the backend. Await on logout while JWT is still valid.
   */
  async unregisterToken(): Promise<void> {
    if (!this.lastRegisteredToken) return;
    const token = this.lastRegisteredToken;
    this.lastRegisteredToken = null;
    this.backendRegisteredToken = null;
    this.logger.info(`[FCM-E2E][Vendor] POST notifications/unregister-token tokenLen=${token.length}`);
    try {
      await firstValueFrom(this.apiNotifications.unregisterFCMToken(token));
      this.logger.info('[FCM-E2E][Vendor] unregister-token API success');
    } catch (err: any) {
      this.logger.warn(`[FCM-E2E][Vendor] unregister-token API failed: ${err?.message || err}`);
    }
  }
}
